import { Link } from "react-router-dom";
import { ArrowRight, Clock, Layers, MapPin, Users } from "lucide-react";
import { track } from "@/lib/api";

export const SectionHeader = ({ eyebrow, title, lead, align = "left", as: Tag = "h2", children }) => (
  <div className={`max-w-3xl mb-12 ${align === "center" ? "mx-auto text-center" : ""}`}>
    {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
    <Tag className="h2" data-testid={Tag === "h1" ? "page-title" : undefined}>{title}</Tag>
    {lead && <p className="lead mt-4">{lead}</p>}
    {children}
  </div>
);

export const ProgramCard = ({ c, compact }) => (
  <article data-testid={`program-card-${c.slug}`} className="card card-hover p-6 flex flex-col h-full group">
    <div className="flex items-start justify-between mb-4">
      <span className="font-mono text-xs text-slate-500">{c.code} — {c.category}</span>
      <span className="chip">{c.level}</span>
    </div>
    <h3 className="text-xl font-semibold text-slate-50 mb-2">{c.title}</h3>
    <p className="text-sm text-slate-400 leading-relaxed mb-5 flex-1">{c.tagline}</p>
    <dl className="grid grid-cols-2 gap-y-2 gap-x-3 text-xs text-slate-300 mb-5">
      <div className="flex items-center gap-1.5"><Clock size={13} className="text-[var(--blue)]" /><dd>{c.duration}</dd></div>
      <div className="flex items-center gap-1.5"><Layers size={13} className="text-[var(--blue)]" /><dd>{c.modules_count} modules · {c.sessions_count} sessions</dd></div>
      <div className="flex items-center gap-1.5"><MapPin size={13} className="text-[var(--blue)]" /><dd>{c.location}</dd></div>
      <div className="flex items-center gap-1.5"><Users size={13} className="text-[var(--blue)]" /><dd>{c.mentor}</dd></div>
    </dl>
    {!compact && (
      <>
        <p className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-1">Mode</p>
        <p className="text-xs text-slate-300 mb-4">{(c.mode || []).join(" · ")}</p>
        <p className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-1">Upcoming batch</p>
        <p className="text-xs text-slate-300 mb-5">{c.next_batch}</p>
      </>
    )}
    <Link to={`/programs/${c.slug}`} data-testid={`program-card-${c.slug}-view`} onClick={() => track("cta_click", { cta: "view_program", slug: c.slug })} className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-[var(--blue)] group-hover:gap-3 transition-[gap]">View Program <ArrowRight size={16} /></Link>
  </article>
);

export const Disclaimer = ({ text, light }) => (
  <p data-testid="disclaimer-text" className={`text-xs leading-relaxed rounded-xl border p-4 ${light ? "border-slate-200 bg-white text-slate-500" : "border-white/10 bg-white/[0.03] text-slate-500"}`}>
    <strong className={light ? "text-slate-700" : "text-slate-300"}>Disclaimer: </strong>{text}
  </p>
);

export const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" className="font-mono text-xs text-slate-500 mb-6"><ol className="flex flex-wrap gap-2">
    {items.map((it, i) => <li key={it.path} className="flex gap-2">{i > 0 && <span>/</span>}{i < items.length - 1 ? <Link to={it.path} className="hover:text-[var(--blue)]">{it.name}</Link> : <span className="text-slate-300">{it.name}</span>}</li>)}
  </ol></nav>
);

export const Markdown = ({ text }) => {
  const blocks = text.split(/\n\n+/);
  return <div className="prose-hot">{blocks.map((b, i) => {
    if (b.startsWith("## ")) return <h2 key={i} id={b.slice(3).toLowerCase().replace(/[^a-z0-9]+/g, "-")}>{b.slice(3)}</h2>;
    if (b.startsWith("### ")) return <h3 key={i}>{b.slice(4)}</h3>;
    if (b.trim().startsWith("- ")) return <ul key={i}>{b.split("\n").map((l, j) => <li key={j}>{l.replace(/^- /, "")}</li>)}</ul>;
    return <p key={i}>{b}</p>;
  })}</div>;
};
